/**
 * CVScorePanel — "Skoor & tagasiside" for the CV Builder.
 *
 * Behaviour:
 *   - `POST /api/cv/score` with the current draft and UI language.
 *   - Displays 0–100 total, 4 pillars (structure / clarity / completeness /
 *     impact, 0–25 each), and exactly 3 actionable tips.
 *   - Caches the last response per content-hash so navigation and re-renders
 *     don't spam the LLM.
 *   - "Uuenda skoor" (Update score) forces a fresh call.
 *   - Each tip with a `fix_action` surfaces a "Fix it" button that opens a
 *     Before/After dialog; user must explicitly confirm "Apply" before we
 *     mutate the draft. No action runs automatically.
 *
 * Fix actions implemented client-side (all use /api/cv/ai):
 *   - rewrite_summary   → action "summary" with the draft snapshot
 *   - expand_experience → action "expand_experience" per experience item;
 *                         if no experience exists we no-op with a toast.
 *   - add_skills        → action "suggest_skills" with the raw CV text
 *   - add_languages     → adds empty language row (user fills it)
 *   - add_certificates  → adds empty certificate row
 *   - format_cv         → action "format_cv" on the stitched CV text
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, RefreshCw, ChevronDown, ChevronUp, Loader2, CheckCircle,
  AlertCircle, PenLine, ListPlus, Languages, Award, Wand2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useLanguage } from '@/lib/context';

const API = process.env.REACT_APP_BACKEND_URL;

// Labels for the panel itself — kept local to guarantee no humanized keys
// ever leak even if a translation is missing.
const L = {
  et: {
    title: 'Skoor & tagasiside',
    subtitle: 'Skoor põhineb CV struktuuril, selgusel ja täidetusel.',
    pillar_structure: 'Struktuur',
    pillar_clarity: 'Selgus',
    pillar_completeness: 'Täidetus',
    pillar_impact: 'Tööandjale mõju',
    run: 'Arvuta skoor',
    update: 'Uuenda skoor',
    running: 'Arvutan…',
    no_score: 'Vajuta "Arvuta skoor", et hinnang kuvada.',
    fix_it: 'Paranda',
    apply: 'Rakenda',
    cancel: 'Tühista',
    before: 'Enne',
    after: 'Pärast',
    preview_apply: 'Eelvaade ja kinnita',
    error: 'Skoori arvutamine ebaõnnestus',
    action_rewrite_summary: 'Paranda kokkuvõte',
    action_expand_experience: 'Täpsem töökogemus',
    action_add_skills: 'Lisa oskused',
    action_add_languages: 'Lisa keel',
    action_add_certificates: 'Lisa tunnistus',
    action_format_cv: 'Vorminda CV',
    applied: 'Rakendatud',
  },
  en: {
    title: 'Score & feedback',
    subtitle: 'Score reflects structure, clarity, completeness and impact.',
    pillar_structure: 'Structure', pillar_clarity: 'Clarity',
    pillar_completeness: 'Completeness', pillar_impact: 'Impact',
    run: 'Run score', update: 'Update score', running: 'Working…',
    no_score: 'Click "Run score" to evaluate the CV.',
    fix_it: 'Fix it', apply: 'Apply', cancel: 'Cancel',
    before: 'Before', after: 'After',
    preview_apply: 'Preview and confirm',
    error: 'Could not score this CV',
    action_rewrite_summary: 'Rewrite summary',
    action_expand_experience: 'Expand experience',
    action_add_skills: 'Add skills',
    action_add_languages: 'Add language',
    action_add_certificates: 'Add certificate',
    action_format_cv: 'Format CV',
    applied: 'Applied',
  },
  fi: {
    title: 'Pisteet & palaute',
    subtitle: 'Pisteet perustuvat rakenteeseen, selkeyteen ja kattavuuteen.',
    pillar_structure: 'Rakenne', pillar_clarity: 'Selkeys',
    pillar_completeness: 'Kattavuus', pillar_impact: 'Vaikutus',
    run: 'Laske pisteet', update: 'Päivitä pisteet', running: 'Työskentelen…',
    no_score: 'Paina "Laske pisteet" saadaksesi arvion.',
    fix_it: 'Korjaa', apply: 'Ota käyttöön', cancel: 'Peruuta',
    before: 'Ennen', after: 'Jälkeen',
    preview_apply: 'Esikatsele ja vahvista',
    error: 'Pisteytys epäonnistui',
    action_rewrite_summary: 'Paranna profiili',
    action_expand_experience: 'Laajenna työkokemus',
    action_add_skills: 'Lisää taitoja',
    action_add_languages: 'Lisää kieli',
    action_add_certificates: 'Lisää sertifikaatti',
    action_format_cv: 'Muotoile CV',
    applied: 'Käytössä',
  },
  ru: {
    title: 'Оценка и советы',
    subtitle: 'Оценка основана на структуре, ясности и полноте.',
    pillar_structure: 'Структура', pillar_clarity: 'Ясность',
    pillar_completeness: 'Полнота', pillar_impact: 'Впечатление',
    run: 'Оценить', update: 'Обновить оценку', running: 'Работаю…',
    no_score: 'Нажмите «Оценить», чтобы получить оценку.',
    fix_it: 'Исправить', apply: 'Применить', cancel: 'Отмена',
    before: 'До', after: 'После',
    preview_apply: 'Предпросмотр и подтверждение',
    error: 'Не удалось оценить CV',
    action_rewrite_summary: 'Переписать резюме',
    action_expand_experience: 'Развернуть опыт',
    action_add_skills: 'Добавить навыки',
    action_add_languages: 'Добавить язык',
    action_add_certificates: 'Добавить сертификат',
    action_format_cv: 'Отформатировать CV',
    applied: 'Применено',
  },
  nb: {
    title: 'Poengsum og tilbakemelding',
    subtitle: 'Poengsummen reflekterer struktur, klarhet, fullstendighet og effekt.',
    pillar_structure: 'Struktur', pillar_clarity: 'Klarhet',
    pillar_completeness: 'Fullstendighet', pillar_impact: 'Effekt',
    run: 'Beregn poengsum', update: 'Oppdater poengsum', running: 'Jobber…',
    no_score: 'Klikk «Beregn poengsum» for å evaluere CV-en.',
    fix_it: 'Fiks det', apply: 'Bruk', cancel: 'Avbryt',
    before: 'Før', after: 'Etter',
    preview_apply: 'Forhåndsvis og bekreft',
    error: 'Kunne ikke poengsette denne CV-en',
    action_rewrite_summary: 'Skriv om sammendraget',
    action_expand_experience: 'Utvid erfaring',
    action_add_skills: 'Legg til ferdigheter',
    action_add_languages: 'Legg til språk',
    action_add_certificates: 'Legg til sertifikat',
    action_format_cv: 'Formater CV',
    applied: 'Brukt',
  },
  sv: {
    title: 'Poäng och feedback',
    subtitle: 'Poängen baseras på struktur, tydlighet, fullständighet och effekt.',
    pillar_structure: 'Struktur', pillar_clarity: 'Tydlighet',
    pillar_completeness: 'Fullständighet', pillar_impact: 'Effekt',
    run: 'Räkna poäng', update: 'Uppdatera poäng', running: 'Arbetar…',
    no_score: 'Klicka på "Räkna poäng" för att utvärdera CV:n.',
    fix_it: 'Åtgärda', apply: 'Tillämpa', cancel: 'Avbryt',
    before: 'Före', after: 'Efter',
    preview_apply: 'Förhandsgranska och bekräfta',
    error: 'Kunde inte poängsätta denna CV',
    action_rewrite_summary: 'Skriv om sammanfattningen',
    action_expand_experience: 'Utöka erfarenhet',
    action_add_skills: 'Lägg till färdigheter',
    action_add_languages: 'Lägg till språk',
    action_add_certificates: 'Lägg till intyg',
    action_format_cv: 'Formatera CV',
    applied: 'Tillämpat',
  },
};
function pick(lang) { return L[lang] || L.en; }

// Cheap content hash so we don't re-score identical drafts.
function draftHash(draft) {
  const shape = {
    fn: draft.first_name, ln: draft.last_name, jt: draft.job_title,
    em: draft.email, ph: draft.phone, lo: draft.location, co: draft.country,
    su: draft.summary,
    ex: (draft.experience || []).map((e) => `${e.company}|${e.position}|${e.start_date}|${e.end_date}|${e.description}`).join('##'),
    ed: (draft.education || []).map((e) => `${e.school}|${e.field}|${e.start_date}|${e.end_date}|${e.notes}`).join('##'),
    sk: (draft.skills || []).join(','),
    lg: (draft.languages || []).map((l) => `${l.language}|${l.level}`).join('##'),
    cr: (draft.certificates || []).map((c) => `${c.name}|${c.issuer}|${c.date}`).join('##'),
    hb: draft.hobbies, ph_: draft.photo_id,
  };
  return JSON.stringify(shape);
}

const pillarOrder = [
  ['structure', PenLine],
  ['clarity', ListPlus],
  ['completeness', Award],
  ['impact', Wand2],
];

export const CVScorePanel = ({ draft, setDraft, clientId }) => {
  const { language } = useLanguage();
  const s = pick(language);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const cacheRef = useRef({});

  const [actionState, setActionState] = useState({
    tip: null, busy: false, before: '', after: '',
  });

  const runScore = useCallback(async (force = false) => {
    const hash = draftHash(draft);
    if (!force && cacheRef.current[hash]) {
      setResult(cacheRef.current[hash]);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API}/api/cv/score`, {
        draft,
        ui_language: language,
      });
      cacheRef.current[hash] = r.data;
      setResult(r.data);
    } catch (e) {
      setError(s.error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, language]);

  // First render: one lazy run if we already have enough content
  useEffect(() => {
    const enough =
      draft.first_name || draft.last_name || (draft.summary || '').length > 40 ||
      (draft.experience || []).length > 0 || (draft.skills || []).length > 0;
    if (!enough) return;
    runScore(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = result?.score_total ?? 0;
  const pillars = result?.pillars || {};
  const tips = result?.top_3_tips || [];

  const scoreColor =
    total >= 75 ? 'bg-emerald-100 text-emerald-700'
    : total >= 50 ? 'bg-amber-100 text-amber-700'
    : 'bg-orange-100 text-orange-700';

  // ------------------------------------------------------------------
  // Fix actions — every one requires an explicit "Apply"
  // ------------------------------------------------------------------
  const startAction = async (tip) => {
    const action = tip.fix_action || 'none';
    if (action === 'none') return;

    // Actions that don't need an LLM roundtrip — just add an empty row
    if (action === 'add_languages') {
      setActionState({ tip, busy: false, before: '', after: '__ADD_ROW_LANG__' });
      return;
    }
    if (action === 'add_certificates') {
      setActionState({ tip, busy: false, before: '', after: '__ADD_ROW_CERT__' });
      return;
    }

    setActionState({ tip, busy: true, before: '', after: '' });
    try {
      if (action === 'rewrite_summary') {
        const before = draft.summary || '';
        // summary action takes a JSON snapshot
        const r = await axios.post(`${API}/api/cv/ai`, {
          action: 'summary',
          text: JSON.stringify({
            job_title: draft.job_title,
            experience: draft.experience,
            education: draft.education,
            skills: draft.skills,
            languages: draft.languages,
          }),
        });
        setActionState({ tip, busy: false, before, after: (r.data?.text || '').trim() });
        return;
      }

      if (action === 'expand_experience') {
        const idx = (draft.experience || []).findIndex(
          (e) => (e.description || '').trim().length < 120
        );
        const target = idx >= 0 ? draft.experience[idx] : (draft.experience || [])[0];
        if (!target) {
          toast.error('—');
          setActionState({ tip: null, busy: false, before: '', after: '' });
          return;
        }
        const before = target.description || '';
        const input =
          before ||
          `${target.position || ''} at ${target.company || ''} (${target.start_date || ''} – ${target.end_date || ''})`;
        const r = await axios.post(`${API}/api/cv/ai`, {
          action: 'expand_experience',
          text: input,
        });
        setActionState({
          tip: { ...tip, _expIdx: idx >= 0 ? idx : 0 },
          busy: false,
          before, after: (r.data?.text || '').trim(),
        });
        return;
      }

      if (action === 'add_skills') {
        const raw = [
          draft.summary,
          ...(draft.experience || []).map((e) => `${e.position} ${e.company} ${e.description}`),
          ...(draft.education || []).map((e) => `${e.school} ${e.field} ${e.notes}`),
          ...(draft.certificates || []).map((c) => `${c.name} ${c.issuer} ${c.notes}`),
          draft.hobbies,
        ].filter(Boolean).join('\n');
        const before = (draft.skills || []).join(', ');
        const r = await axios.post(`${API}/api/cv/ai`, {
          action: 'suggest_skills',
          text: raw,
        });
        const after = (r.data?.text || '').trim();
        setActionState({ tip, busy: false, before, after });
        return;
      }

      if (action === 'format_cv') {
        // Stitch the CV into a single text, let LLM reformat, show diff-ish.
        const before = [
          draft.summary,
          ...(draft.experience || []).map((e) => `${e.position} @ ${e.company} (${e.start_date}-${e.end_date})\n${e.description}`),
          (draft.skills || []).length ? `Skills: ${(draft.skills || []).join(', ')}` : '',
        ].filter(Boolean).join('\n\n');
        const r = await axios.post(`${API}/api/cv/ai`, {
          action: 'format_cv',
          text: before,
        });
        setActionState({ tip, busy: false, before, after: (r.data?.text || '').trim() });
        return;
      }
    } catch (e) {
      toast.error(s.error);
      setActionState({ tip: null, busy: false, before: '', after: '' });
    }
  };

  const applyAction = () => {
    const { tip, after } = actionState;
    if (!tip) return;
    const action = tip.fix_action;

    if (action === 'rewrite_summary') {
      setDraft((d) => ({ ...d, summary: after }));
    } else if (action === 'expand_experience') {
      const i = tip._expIdx ?? 0;
      setDraft((d) => {
        const list = [...(d.experience || [])];
        if (list[i]) {
          list[i] = { ...list[i], description: after };
        }
        return { ...d, experience: list };
      });
    } else if (action === 'add_skills') {
      const list = after.split(',').map((x) => x.trim()).filter(Boolean);
      setDraft((d) => {
        const merged = Array.from(new Set([...(d.skills || []), ...list]));
        return { ...d, skills: merged };
      });
    } else if (action === 'add_languages') {
      setDraft((d) => ({ ...d, languages: [...(d.languages || []), { language: '', level: '' }] }));
    } else if (action === 'add_certificates') {
      setDraft((d) => ({ ...d, certificates: [...(d.certificates || []), { name: '', issuer: '', date: '', notes: '' }] }));
    } else if (action === 'format_cv') {
      // Non-destructive: we only improve the summary + first experience desc
      setDraft((d) => ({ ...d, summary: after.slice(0, 800) || d.summary }));
    }
    toast.success(s.applied);
    setActionState({ tip: null, busy: false, before: '', after: '' });
    // Score must be re-run after a real change
    setTimeout(() => runScore(true), 600);
  };

  const cancelAction = () =>
    setActionState({ tip: null, busy: false, before: '', after: '' });

  const actionLabel = (a) => {
    switch (a) {
      case 'rewrite_summary': return s.action_rewrite_summary;
      case 'expand_experience': return s.action_expand_experience;
      case 'add_skills': return s.action_add_skills;
      case 'add_languages': return s.action_add_languages;
      case 'add_certificates': return s.action_add_certificates;
      case 'format_cv': return s.action_format_cv;
      default: return s.fix_it;
    }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 shadow-sm"
      data-testid="cv-score-panel"
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 md:hidden"
        data-testid="cv-score-toggle"
      >
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Sparkles className="h-4 w-4 text-orange-500" /> {s.title}
        </span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      <div className={`${open ? '' : 'hidden'} md:block p-5`}>
        <div className="hidden md:flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Sparkles className="h-4 w-4 text-orange-500" />
          <span>{s.title}</span>
        </div>

        <p className="text-xs text-slate-500 mt-1">{s.subtitle}</p>

        <div className="mt-3 flex items-center gap-3">
          <div
            className={`inline-flex items-center justify-center w-14 h-14 rounded-full text-xl font-bold ${scoreColor}`}
            data-testid="cv-score-total"
          >
            {result ? total : '—'}
          </div>
          <div className="flex-1">
            <div className="text-[11px] text-slate-500 mb-1">0 – 100</div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => runScore(true)}
              disabled={loading}
              data-testid="cv-score-run"
              className="h-8"
            >
              {loading
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />{s.running}</>
                : <><RefreshCw className="h-3.5 w-3.5 mr-1" />{result ? s.update : s.run}</>
              }
            </Button>
          </div>
        </div>

        {/* Pillars */}
        <div className="mt-4 grid grid-cols-2 gap-2" data-testid="cv-score-pillars">
          {pillarOrder.map(([key, Icon]) => {
            const v = pillars[key] ?? 0;
            const pct = Math.round((v / 25) * 100);
            return (
              <div key={key} className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                <div className="flex items-center justify-between text-[11px] text-slate-600">
                  <span className="inline-flex items-center gap-1">
                    <Icon className="h-3 w-3" />
                    {s[`pillar_${key}`]}
                  </span>
                  <span className="font-semibold text-slate-800">{v}/25</span>
                </div>
                <div className="mt-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Tips */}
        <div className="mt-4 space-y-2" data-testid="cv-score-tips">
          {!result && !loading && (
            <div className="text-xs text-slate-500 text-center py-4">{s.no_score}</div>
          )}
          {result && tips.map((tip, i) => (
            <div
              key={i}
              className="border border-slate-200 rounded-xl px-3 py-2.5"
              data-testid={`cv-score-tip-${i}`}
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 leading-snug">
                    {tip.title}
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5 leading-snug">{tip.why}</p>
                  {tip.example && (
                    <p className="text-[11px] text-slate-500 mt-1 italic">"{tip.example}"</p>
                  )}
                </div>
              </div>
              {tip.fix_action && tip.fix_action !== 'none' && (
                <div className="mt-2 flex justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-orange-600 hover:text-orange-700"
                    onClick={() => startAction(tip)}
                    data-testid={`cv-score-fix-${i}`}
                  >
                    {actionLabel(tip.fix_action)}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>

        {error && (
          <div className="mt-3 text-xs text-red-600">{error}</div>
        )}
      </div>

      {/* Apply preview dialog */}
      <Dialog
        open={!!actionState.tip}
        onOpenChange={(o) => { if (!o) cancelAction(); }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-orange-500" />
              {s.preview_apply}
            </DialogTitle>
            <DialogDescription>
              {actionState.tip && actionLabel(actionState.tip.fix_action)}
            </DialogDescription>
          </DialogHeader>

          {actionState.busy ? (
            <div className="py-10 flex items-center justify-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              {s.running}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3" data-testid="cv-apply-preview">
              <div>
                <div className="text-[11px] font-semibold uppercase text-slate-500 mb-1">
                  {s.before}
                </div>
                <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap max-h-64 overflow-y-auto min-h-[96px]">
                  {actionState.before || '—'}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase text-emerald-700 mb-1">
                  {s.after}
                </div>
                <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-3 whitespace-pre-wrap max-h-64 overflow-y-auto min-h-[96px]">
                  {actionState.after === '__ADD_ROW_LANG__'
                    ? '+ language row'
                    : actionState.after === '__ADD_ROW_CERT__'
                    ? '+ certificate row'
                    : (actionState.after || '—')}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelAction} data-testid="cv-apply-cancel">
              {s.cancel}
            </Button>
            <Button
              onClick={applyAction}
              disabled={actionState.busy || !actionState.after}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="cv-apply-confirm"
            >
              {s.apply}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CVScorePanel;
